import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  DatePicker,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  HistoryOutlined,
  KeyOutlined,
  LoginOutlined,
  LogoutOutlined,
  PlusOutlined,
  ReloadOutlined,
  SwapOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import type { ActivityLogEntry, ActivitySource, AdminUser } from '@lens/shared';
import { useAuth } from '../auth/AuthContext.tsx';
import { colors, radius, shadows, spacing } from '../../shared/theme/tokens.ts';
import { listActivity, listAdminUsers } from './api.ts';
import { useDocumentTitle } from '../../shared/hooks/useDocumentTitle.ts';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PAGE_SIZE = 50;

/** Нейтральный чип для типа сущности / пространства в колонке «Объект». */
const objectTypeChipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  background: 'var(--color-bg-layout)',
  color: 'var(--color-text-secondary)',
  borderRadius: radius.sm,
  padding: '1px 8px',
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: 'nowrap',
};

// Человекочитаемые подписи кодов действий обоих источников.
const ACTION_LABELS: Record<string, string> = {
  // record (audit_log)
  created: 'Создана запись',
  field_change: 'Изменено поле',
  status_change: 'Смена статуса',
  deleted: 'Удалена запись',
  bulk_update: 'Массовое изменение',
  link_created: 'Создана связь',
  link_deleted: 'Удалена связь',
  jira_sync: 'Синхронизация с Jira',
  // auth (auth_log)
  login_success: 'Успешный вход',
  login_failure: 'Неудачный вход',
  logout: 'Выход',
  password_change: 'Смена пароля',
  user_created: 'Создан пользователь',
  user_updated: 'Изменён пользователь',
  user_disabled: 'Пользователь отключён',
  password_reset_by_admin: 'Сброс пароля админом',
  platform_role_changed: 'Смена платформенной роли',
  sso_login: 'Вход через Keycloak',
  sso_linked: 'Связан с Keycloak',
  sso_provisioned: 'Заведён из Keycloak',
  sso_denied_no_access: 'Отказ: нет роли доступа',
  // access (access_log)
  space_opened: 'Открыто пространство',
  item_opened: 'Открыта карточка',
};

const SOURCE_LABELS: Record<ActivitySource, string> = {
  record: 'Запись',
  auth: 'Аккаунт',
  config: 'Конфигурация',
  access: 'Открытие',
};

// Для config-источника коды действий совпадают по написанию с record
// ('created'/'updated'/'deleted'), но читаются иначе — поэтому отдельная карта.
const CONFIG_ACTION_LABELS: Record<string, string> = {
  created: 'Создано',
  updated: 'Изменено',
  deleted: 'Удалено',
  archived: 'Архивировано',
  unarchived: 'Разархивировано',
  duplicated: 'Дублировано',
  role_changed: 'Смена роли',
};

// Тип конфигурационной сущности → подпись.
const ENTITY_TYPE_LABELS: Record<string, string> = {
  space: 'Пространство',
  space_member: 'Участник',
  space_group: 'Группа Keycloak',
  space_user_group: 'Группа пользователей',
  item_type: 'Тип записи',
  field: 'Поле',
  dictionary: 'Справочник',
  dictionary_column: 'Колонка справочника',
  dictionary_editor: 'Редактор справочника',
  dictionary_viewer: 'Наблюдатель справочника',
  view: 'Представление',
};

function actionLabel(action: string, source: ActivitySource): string {
  if (source === 'config') return CONFIG_ACTION_LABELS[action] ?? action;
  return ACTION_LABELS[action] ?? action;
}

// Опции фильтра «Действие», сгруппированные по источнику. Значения уникальны:
// коды, общие у record и config (`created`/`deleted`), перечислены один раз
// (под «Записи») — бэкенд фильтрует по сырому коду, поэтому такой фильтр ловит
// оба источника; `updated` — только config. Открытия (`space_opened`/
// `item_opened`) уникальны и вынесены отдельной группой.
const ACTION_FILTER_GROUPS: Array<{ label: string; options: Array<[string, string]> }> = [
  {
    label: 'Открытия',
    options: [
      ['space_opened', 'Открыто пространство'],
      ['item_opened', 'Открыта карточка'],
    ],
  },
  {
    label: 'Записи',
    options: [
      ['created', 'Создана запись'],
      ['field_change', 'Изменено поле'],
      ['status_change', 'Смена статуса'],
      ['deleted', 'Удалена запись'],
      ['bulk_update', 'Массовое изменение'],
      ['link_created', 'Создана связь'],
      ['link_deleted', 'Удалена связь'],
      ['jira_sync', 'Синхронизация с Jira'],
    ],
  },
  {
    label: 'Конфигурация',
    options: [
      ['updated', 'Изменено'],
      ['archived', 'Архивировано'],
      ['unarchived', 'Разархивировано'],
      ['duplicated', 'Дублировано'],
      ['role_changed', 'Смена роли'],
    ],
  },
  {
    label: 'Аккаунты',
    options: [
      ['login_success', 'Успешный вход'],
      ['login_failure', 'Неудачный вход'],
      ['logout', 'Выход'],
      ['password_change', 'Смена пароля'],
      ['user_created', 'Создан пользователь'],
      ['user_updated', 'Изменён пользователь'],
      ['user_disabled', 'Пользователь отключён'],
      ['password_reset_by_admin', 'Сброс пароля админом'],
      ['platform_role_changed', 'Смена платформенной роли'],
      ['sso_login', 'Вход через Keycloak'],
      ['sso_linked', 'Связан с Keycloak'],
      ['sso_provisioned', 'Заведён из Keycloak'],
      ['sso_denied_no_access', 'Отказ: нет роли доступа'],
    ],
  },
];

const ACTION_FILTER_OPTIONS = ACTION_FILTER_GROUPS.map((g) => ({
  label: g.label,
  options: g.options.map(([value, label]) => ({ value, label })),
}));

/** Иконка + цвет для типа действия — мгновенная визуальная классификация строки. */
function actionVisual(action: string): { icon: JSX.Element; color: string } {
  if (['created', 'user_created', 'link_created'].includes(action))
    return { icon: <PlusOutlined />, color: 'var(--color-success)' };
  if (action === 'login_success') return { icon: <LoginOutlined />, color: 'var(--color-success)' };
  if (['deleted', 'link_deleted', 'user_disabled'].includes(action))
    return { icon: <DeleteOutlined />, color: 'var(--color-error)' };
  if (action === 'login_failure') return { icon: <LoginOutlined />, color: 'var(--color-error)' };
  if (action === 'logout') return { icon: <LogoutOutlined />, color: colors.textSecondary };
  if (['space_opened', 'item_opened'].includes(action))
    return { icon: <EyeOutlined />, color: colors.teal };
  if (action === 'jira_sync') return { icon: <SyncOutlined />, color: colors.teal };
  if (['status_change', 'role_changed', 'platform_role_changed'].includes(action))
    return { icon: <SwapOutlined />, color: 'var(--color-warning)' };
  if (['password_change', 'password_reset_by_admin'].includes(action))
    return { icon: <KeyOutlined />, color: 'var(--color-warning)' };
  return { icon: <EditOutlined />, color: 'var(--color-info)' };
}

/** Человекочитаемые подписи ключей в снимке config-объекта. */
const OBJECT_KEY_LABELS: Record<string, string> = {
  name: 'Название',
  slug: 'Ключ',
  fieldType: 'Тип',
  type: 'Тип',
  role: 'Роль',
  login: 'Логин',
  displayName: 'Имя',
  owner: 'Владелец',
  displayColumn: 'Колонка отображения',
  keyColumn: 'Ключевая колонка',
  required: 'Обязательное',
  hasCategories: 'Со статусами',
};

/** Технические ключи-идентификаторы (id, *Id, метки времени) — шум, скрываем из снимка. */
function isNoiseKey(key: string): boolean {
  return key === 'id' || /Id$/.test(key) || key === 'createdAt' || key === 'updatedAt';
}

/** Детерминированная палитра аватаров по логину/имени актора. */
const AVATAR_COLORS = [
  'var(--color-primary)',
  'var(--color-info)',
  'var(--color-success)',
  'var(--color-warning)',
  colors.teal,
  'var(--color-error)',
];

function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length] ?? 'var(--color-primary)';
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

/** Относительное время для свежих событий, дата — для старых. */
function relativeTime(iso: string): string {
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 45) return 'только что';
  if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)} дн назад`;
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

/** Максимальная длина значения, показываемого прямо в строке журнала. */
const VALUE_PREVIEW_LIMIT = 80;

/** Порог, выше которого diff «старое → новое» переносим в столбик, чтобы стрелка не отрывалась. */
const INLINE_CHANGE_LIMIT = 32;

/** Компактное строковое представление произвольного значения поля. */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value === '' ? '—' : value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  if (Array.isArray(value)) return value.length ? value.map(formatValue).join(', ') : '—';
  if (typeof value === 'object') {
    // Снимок config-объекта рендерим как «Ключ: значение», а не сырой JSON.
    const parts = Object.entries(value as Record<string, unknown>)
      .filter(([k, v]) => v !== null && v !== undefined && v !== '' && !isNoiseKey(k))
      .map(([k, v]) => `${OBJECT_KEY_LABELS[k] ?? k}: ${formatValue(v)}`);
    return parts.length ? parts.join(' · ') : '—';
  }
  return String(value);
}

/** Однострочное превью значения: схлопывает переводы строк и обрезает длинный текст. */
function previewValue(raw: string): { short: string; truncated: boolean } {
  const oneLine = raw.replace(/\s+/g, ' ').trim();
  if (oneLine.length <= VALUE_PREVIEW_LIMIT) return { short: oneLine, truncated: false };
  return { short: `${oneLine.slice(0, VALUE_PREVIEW_LIMIT)}…`, truncated: true };
}

/** Значение поля: короткое — инлайном, длинное — обрезано с раскрытием инлайн по клику.
 * Текст всегда переносится по словам и не вылезает за пределы колонки. */
function ValuePart({ value }: { value: unknown }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const full = formatValue(value);
  const { short, truncated } = previewValue(full);
  if (!truncated) return <span style={{ wordBreak: 'break-word' }}>{short}</span>;
  return (
    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {expanded ? full : short}{' '}
      <Text
        onClick={(ev) => {
          ev.stopPropagation();
          setExpanded((v) => !v);
        }}
        style={{
          fontSize: 12,
          color: 'var(--color-primary)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {expanded ? 'свернуть' : 'развернуть'}
      </Text>
    </span>
  );
}

function ValueChange({ entry }: { entry: ActivityLogEntry }): JSX.Element | null {
  const { oldValue, newValue } = entry;
  if (oldValue == null && newValue == null) return null;
  // Создание: значения ещё нет — показываем только новое, без «— →».
  if (oldValue == null) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        <ValuePart value={newValue} />
      </Text>
    );
  }
  // Удаление: нового значения нет — старое зачёркнутым.
  if (newValue == null) {
    return (
      <Text type="secondary" delete style={{ fontSize: 12 }}>
        <ValuePart value={oldValue} />
      </Text>
    );
  }
  // Если хотя бы одно значение длинное — переносим diff в столбик, иначе стрелка
  // отрывается на отдельную строку и колонка выглядит сломанной.
  const oldLen = previewValue(formatValue(oldValue)).short.length;
  const newLen = previewValue(formatValue(newValue)).short.length;
  const stacked = oldLen + newLen > INLINE_CHANGE_LIMIT;

  if (stacked) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Text type="secondary" delete style={{ fontSize: 12 }}>
          <ValuePart value={oldValue} />
        </Text>
        <Text type="secondary" style={{ fontSize: 12, display: 'flex', gap: 4 }}>
          <span style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }}>→</span>
          <ValuePart value={newValue} />
        </Text>
      </div>
    );
  }

  return (
    <Text type="secondary" style={{ fontSize: 12 }}>
      <ValuePart value={oldValue} />
      <Text type="secondary" style={{ fontSize: 12, margin: '0 4px' }}>
        →
      </Text>
      <ValuePart value={newValue} />
    </Text>
  );
}

export function ActivityLogPage(): JSX.Element {
  useDocumentTitle('Журнал действий');
  const { user: currentUser } = useAuth();
  const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  // Фильтры
  const [source, setSource] = useState<ActivitySource | undefined>();
  const [action, setAction] = useState<string | undefined>();
  const [userId, setUserId] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);

  // Чтобы «Загрузить ещё» использовал актуальные фильтры без лишних ререндеров.
  const filtersRef = useRef({ source, action, userId, range });
  filtersRef.current = { source, action, userId, range };

  const buildParams = useCallback((before?: string) => {
    const { source: s, action: a, userId: u, range: r } = filtersRef.current;
    return {
      source: s,
      action: a,
      userId: u,
      from: r?.[0]?.startOf('day').toISOString(),
      to: r?.[1]?.endOf('day').toISOString(),
      before,
      limit: PAGE_SIZE,
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listActivity(buildParams());
      setEntries(data.entries);
      setNextCursor(data.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить журнал');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const data = await listActivity(buildParams(nextCursor));
      setEntries((prev) => [...prev, ...data.entries]);
      setNextCursor(data.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить ещё');
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, nextCursor]);

  useEffect(() => {
    void load();
  }, [load, source, action, userId, range]);

  useEffect(() => {
    if (!currentUser?.isPlatformAdmin) return;
    listAdminUsers()
      .then((d) => setUsers(d.users))
      .catch(() => {
        /* список пользователей для фильтра не критичен */
      });
  }, [currentUser]);

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: `${u.displayName} (${u.login})`,
      })),
    [users],
  );

  if (!currentUser?.isPlatformAdmin) {
    return (
      <div style={{ width: '100%', padding: `${spacing.lg}px ${spacing.base}px` }}>
        <Alert
          type="error"
          message="Доступ запрещён. Эта страница доступна только администраторам платформы."
        />
      </div>
    );
  }

  const columns: ColumnsType<ActivityLogEntry> = [
    {
      title: 'Время',
      key: 'at',
      width: 150,
      render: (_, e) => (
        <Tooltip title={new Date(e.at).toLocaleString('ru-RU')}>
          <div style={{ lineHeight: 1.35 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--color-text)',
                whiteSpace: 'nowrap',
              }}
            >
              {relativeTime(e.at)}
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--color-text-secondary)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {new Date(e.at).toLocaleTimeString('ru-RU', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Кто',
      key: 'actor',
      width: 210,
      render: (_, e) =>
        e.actor ? (
          <Space size={spacing.sm} align="center">
            <Avatar
              size={32}
              style={{
                background: avatarColor(e.actor.login),
                fontSize: 12,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {initials(e.actor.displayName)}
            </Avatar>
            <div style={{ lineHeight: 1.3, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: 'var(--color-text)' }}>{e.actor.displayName}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{e.actor.login}</div>
            </div>
          </Space>
        ) : (
          <Space size={spacing.sm} align="center">
            <Avatar
              size={32}
              style={{ background: 'var(--color-bg-layout)', color: 'var(--color-text-secondary)', flexShrink: 0 }}
            >
              <SyncOutlined />
            </Avatar>
            <Text type="secondary">Система</Text>
          </Space>
        ),
    },
    {
      title: 'Источник',
      key: 'source',
      width: 124,
      render: (_, e) => {
        // Источники намеренно различимы по цвету (не унифицируем).
        const tone =
          e.source === 'auth'
            ? { bg: 'var(--color-warning-bg)', fg: 'var(--color-warning)' }
            : e.source === 'config'
              ? { bg: 'var(--color-primary-bg)', fg: 'var(--color-primary)' }
              : e.source === 'access'
                ? { bg: 'var(--color-success-bg)', fg: 'var(--color-success)' }
                : { bg: 'var(--color-info-bg)', fg: 'var(--color-info)' };
        return (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: tone.bg,
              color: tone.fg,
              borderRadius: radius.sm,
              padding: '2px 10px',
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: tone.fg,
                flexShrink: 0,
              }}
            />
            {SOURCE_LABELS[e.source]}
          </span>
        );
      },
    },
    {
      title: 'Действие',
      key: 'action',
      width: 210,
      render: (_, e) => {
        const { icon, color } = actionVisual(e.action);
        return (
          <Space size={spacing.sm} align="center" style={{ flexWrap: 'nowrap' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                borderRadius: radius.sm,
                background: `${color}1A`,
                color,
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              {icon}
            </span>
            <Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
              {actionLabel(e.action, e.source)}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Объект',
      key: 'object',
      render: (_, e) => {
        if (e.source === 'auth') {
          return (
            <Space direction="vertical" size={2}>
              <Text style={{ fontSize: 13 }}>{e.targetLogin ?? '—'}</Text>
              {e.ip && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  IP: {e.ip}
                </Text>
              )}
            </Space>
          );
        }
        if (e.source === 'config') {
          return (
            <Space direction="vertical" size={2}>
              <Space size={6} wrap>
                {e.entity && (
                  <span style={objectTypeChipStyle}>
                    {ENTITY_TYPE_LABELS[e.entity.type] ?? e.entity.type}
                  </span>
                )}
                {e.entity?.name && (
                  <Text strong style={{ fontSize: 13 }}>
                    {e.entity.name}
                  </Text>
                )}
              </Space>
              {e.space && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Пространство: {e.space.name}
                </Text>
              )}
              <ValueChange entry={e} />
            </Space>
          );
        }
        return (
          <Space direction="vertical" size={2}>
            <Space size={6} wrap>
              {e.space && <span style={objectTypeChipStyle}>{e.space.name}</span>}
              {e.item?.displayKey && (
                <Text
                  code
                  style={{ fontSize: 12, whiteSpace: 'nowrap', margin: 0 }}
                >
                  {e.item.displayKey}
                </Text>
              )}
              {e.item?.title && (
                <Text strong style={{ fontSize: 13 }}>
                  {e.item.title}
                </Text>
              )}
            </Space>
            {e.field && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Поле: {e.field.name}
              </Text>
            )}
            <ValueChange entry={e} />
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ width: '100%', padding: `${spacing.lg}px ${spacing.base}px` }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: spacing.lg,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: radius.md,
              background: 'var(--color-primary-bg)',
              color: 'var(--color-primary)',
              fontSize: 20,
            }}
          >
            <HistoryOutlined />
          </span>
          <div>
            <Title level={1} style={{ margin: 0, fontSize: 22, lineHeight: 1.2 }}>
              Журнал действий
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              События записей, аккаунтов, конфигурации и открытий
            </Text>
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
          Обновить
        </Button>
      </div>

      <Card
        variant="borderless"
        styles={{ body: { padding: spacing.base } }}
        style={{
          marginBottom: spacing.base,
          background: 'var(--color-bg-container)',
          borderRadius: radius.lg,
          boxShadow: shadows.card,
        }}
      >
        <Space wrap size={spacing.sm}>
          <Select
            allowClear
            placeholder="Сотрудник"
            options={userOptions}
            value={userId}
            onChange={(v) => setUserId(v)}
            showSearch
            optionFilterProp="label"
            style={{ width: 240 }}
            data-testid="activity-filter-user"
          />
          <Select
            allowClear
            placeholder="Источник"
            value={source}
            onChange={(v) => setSource(v)}
            style={{ width: 160 }}
            options={[
              { value: 'record', label: 'Записи' },
              { value: 'auth', label: 'Аккаунты' },
              { value: 'config', label: 'Конфигурация' },
              { value: 'access', label: 'Открытия' },
            ]}
            data-testid="activity-filter-source"
          />
          <Select
            allowClear
            placeholder="Действие"
            value={action}
            onChange={(v) => setAction(v)}
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
            options={ACTION_FILTER_OPTIONS}
            data-testid="activity-filter-action"
          />
          <RangePicker
            value={range}
            onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
            format="DD.MM.YYYY"
          />
        </Space>
      </Card>

      {error && <Alert type="error" message={error} style={{ marginBottom: spacing.base }} />}

      <Card
        variant="borderless"
        styles={{ body: { padding: spacing.xs } }}
        style={{ borderRadius: radius.lg, boxShadow: shadows.card }}
      >
        <Table<ActivityLogEntry>
          dataSource={entries}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="middle"
          locale={{ emptyText: 'Нет событий' }}
        />
      </Card>

      {nextCursor && (
        <div style={{ textAlign: 'center', marginTop: spacing.base }}>
          <Button onClick={() => void loadMore()} loading={loadingMore}>
            Загрузить ещё
          </Button>
        </div>
      )}
    </div>
  );
}
